import React from "react";
import { Form, Table, Empty, Button, InputNumber, Input, Radio, DatePicker } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/es/form";

interface StockFormProps {
  form: FormInstance;
}

export const StockForm: React.FC<StockFormProps> = ({ form }) => {
  return (
    <Form.List name="stocks">
      {(fields, { add, remove }) => {
        const columns = [
          {
            title: "Batch No",
            key: "batch_no",
            width: 140,
            render: (_: any, field: any) => {
              const currentBatchNo = form.getFieldValue([
                "stocks",
                field.name,
                "batch_no",
              ]);
              const isNew =
                !currentBatchNo ||
                currentBatchNo === "BATCH-new" ||
                currentBatchNo.startsWith("TEMP-") ||
                currentBatchNo.includes("new");
              return (
                <div className="flex flex-col py-1 justify-center">
                  {isNew ? (
                    <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-600 rounded border border-blue-100  text-center w-max">
                      Auto-Generated
                    </span>
                  ) : (
                    <span className="font-semibold text-gray-700 text-xs font-mono bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded block w-max">
                      {currentBatchNo}
                    </span>
                  )}
                  <Form.Item {...field} name={[field.name, "batch_no"]} noStyle>
                    <input type="hidden" />
                  </Form.Item>
                </div>
              );
            },
          },
          {
            title: "Qty",
            key: "quantity",
            width: 80,
            render: (_: any, field: any) => (
              <Form.Item
                {...field}
                name={[field.name, "quantity"]}
                style={{ margin: 0 }}
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Qty"
                />
              </Form.Item>
            ),
          },
          {
            title: "Cost",
            key: "cost_price",
            width: 90,
            render: (_: any, field: any) => (
              <Form.Item
                {...field}
                name={[field.name, "cost_price"]}
                style={{ margin: 0 }}
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Cost"
                />
              </Form.Item>
            ),
          },
          {
            title: "Retail",
            key: "retail_price",
            width: 90,
            render: (_: any, field: any) => (
              <Form.Item
                {...field}
                name={[field.name, "retail_price"]}
                style={{ margin: 0 }}
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Retail"
                />
              </Form.Item>
            ),
          },
          {
            title: "Wholesale",
            key: "wholesale_price",
            width: 95,
            render: (_: any, field: any) => (
              <Form.Item
                {...field}
                name={[field.name, "wholesale_price"]}
                style={{ margin: 0 }}
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Whole"
                />
              </Form.Item>
            ),
          },
          {
            title: "Expiry Date",
            key: "expiry_date",
            width: 140,
            render: (_: any, field: any) => (
              <Form.Item
                {...field}
                name={[field.name, "expiry_date"]}
                style={{ margin: 0 }}
              >
                <DatePicker
                  size="small"
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  placeholder="No Expiry"
                />
              </Form.Item>
            ),
          },
          {
            title: "Default",
            key: "is_default",
            width: 70,
            align: "center" as const,
            render: (_: any, field: any) => {
              const currentStocks = form.getFieldValue("stocks") || [];
              const isDefault = currentStocks[field.name]?.is_default === 1;
              return (
                <div className="flex justify-center items-center">
                  <Radio
                    checked={isDefault}
                    onChange={() => {
                      const updated = currentStocks.map(
                        (stock: any, idx: number) => ({
                          ...stock,
                          is_default: idx === field.name ? 1 : 0,
                        }),
                      );
                      form.setFieldsValue({ stocks: updated });
                    }}
                  />
                  <Form.Item
                    {...field}
                    name={[field.name, "is_default"]}
                    noStyle
                  >
                    <input type="hidden" />
                  </Form.Item>
                </div>
              );
            },
          },
          {
            title: "",
            key: "actions",
            width: 40,
            render: (_: any, field: any) => (
              <div className="flex items-center gap-1">
                <Form.Item {...field} name={[field.name, "id"]} noStyle>
                  <Input type="hidden" />
                </Form.Item>
                {fields.length > 1 && (
                  <Button
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    size="small"
                    onClick={() => {
                      const wasDefault =
                        form.getFieldValue([
                          "stocks",
                          field.name,
                          "is_default",
                        ]) === 1;
                      remove(field.name);
                      if (wasDefault) {
                        setTimeout(() => {
                          const currentStocks =
                            form.getFieldValue("stocks") || [];
                          if (currentStocks.length > 0) {
                            const updated = currentStocks.map(
                              (stock: any, idx: number) => ({
                                ...stock,
                                is_default: idx === 0 ? 1 : 0,
                              }),
                            );
                            form.setFieldsValue({ stocks: updated });
                          }
                        }, 0);
                      }
                    }}
                  />
                )}
              </div>
            ),
          },
        ];

        return (
          <div className="flex flex-col gap-3">
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <Table
                dataSource={fields}
                columns={columns}
                pagination={false}
                size="small"
                bordered
                rowKey="key"
                scroll={{ x: 550 }}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No stock batches defined."
                    />
                  ),
                }}
              />
            </div>
            <Button
              type="dashed"
              onClick={() => {
                const currentStocks = form.getFieldValue("stocks") || [];
                add({
                  batch_no: "BATCH-new",
                  quantity: 0,
                  cost_price: 0,
                  retail_price: 0,
                  wholesale_price: 0,
                  is_default: currentStocks.length === 0 ? 1 : 0,
                });
              }}
              block
              icon={<PlusOutlined />}
            >
              Add Stock Batch
            </Button>
          </div>
        );
      }}
    </Form.List>
  );
};
